import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { useFragment } from "react-relay";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";

import { initializeDiningWizardStateAtom } from "../../atoms/actions/initializeDiningWizardState/initializeDiningWizardState";
import { DiningBookingWizard } from "../../types";
import { useInitializeDiningBookingWizard_diningReservationHold$key } from "./__generated__/useInitializeDiningBookingWizard_diningReservationHold.graphql";
import { useInitializeDiningBookingWizard_session$key } from "./__generated__/useInitializeDiningBookingWizard_session.graphql";

export const useInitializeDiningBookingWizard = ({
  diningReservationHoldRef,
  ownerId,
  sessionRef,
}: {
  diningReservationHoldRef?: useInitializeDiningBookingWizard_diningReservationHold$key | null;
  ownerId: string;
  sessionRef?: useInitializeDiningBookingWizard_session$key | null;
}) => {
  const diningReservationHold = useFragment(
    useInitializeDiningBookingWizardFragmentString,
    diningReservationHoldRef
  );
  const initializeDiningWizardState = useSetAtom(
    initializeDiningWizardStateAtom
  );
  const session = useFragment(
    useInitializeDiningBookingWizardSessionFragmentString,
    sessionRef
  );
  const ownerProfile = session?.currentClientProfile?.member;
  const trackEvent = useTrackEvent();
  useEffect(() => {
    if (diningReservationHold) {
      const data: DiningBookingWizard.DiningBookingWizardData = {
        dietaryTags: [],
        occasionTags: [],
        partySize: diningReservationHold.partySize ?? 1,
        notes: "",
        otherPatronIds: [],
        ownerId,
        reservationHoldId: diningReservationHold.id,
      };

      const ownerMemberId = ownerProfile?.id ?? ownerId;
      const ownerKnownPerson = ownerProfile
        ? {
            type: "Member" as const,
            playerId: ownerMemberId,
            firstName: ownerProfile.firstName ?? "",
            lastName: ownerProfile.lastName ?? "",
            profileImagePublicId: ownerProfile.profileImage?.publicId ?? "",
          }
        : null;

      trackEvent("BookingWizard.Initialize", {
        initialBookingWizardData: data,
        requiredSteps:
          data.partySize > 1
            ? ["DiningWizardDetailStep", "DiningAddKnownPeopleStep"]
            : ["DiningWizardDetailStep"],
      });
      initializeDiningWizardState({
        initialDiningBookingWizardData: data,
        knownPeople: ownerKnownPerson ? [ownerKnownPerson] : [],
        ownerMember: ownerKnownPerson,
      });
    }
  }, [
    diningReservationHold,
    initializeDiningWizardState,
    ownerId,
    ownerProfile,
    trackEvent,
  ]);
  return null;
};

export const useInitializeDiningBookingWizardFragmentString = graphql`
  fragment useInitializeDiningBookingWizard_diningReservationHold on DiningReservationHold {
    id
    date
    expiresAt
    partySize
    policy
    time
  }
`;

export const useInitializeDiningBookingWizardSessionFragmentString = graphql`
  fragment useInitializeDiningBookingWizard_session on Session {
    currentClientProfile {
      member {
        ... on Member {
          id
          firstName
          lastName
          profileImage {
            # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
            publicId: renderingKey
          }
        }
      }
    }
  }
`;
